import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
} from "react";
import { AlertCircle } from "lucide-react";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { Toaster } from "sonner";

import { DistrictProvider, useDistrict } from "./lib/district-context";
import { useFloatingState } from "./hooks/useFloatingState";
import { supabase, hasLocalStorage } from "./lib/supabase";
import { clearLocalData } from "./lib/db";
import { getChurches, repairOutbox } from "./lib/api";
import { useMaterialsRealtime } from "./hooks/useMaterialsRealtime";

import Auth from "./pages/Auth";
import RequestAccess from "./pages/RequestAccess";
import RequestSent from "./pages/RequestSent";
import SetPassword from "./pages/SetPassword";
import CheckEmail from "./pages/CheckEmail";
import ForgotPassword from "./pages/ForgotPassword";
import Unauthorized from "./pages/Unauthorized";
import CreateDistrict from "./pages/CreateDistrict";

import SplashScreen from "./components/SplashScreen";
import AppLoader from "./components/AppLoader";
import ScrollToTop from "./components/ScrollToTop";
import { NavHistory } from "./lib/nav-history";
import Layout from "./components/Layout";
import FloatingWizard from "./components/FloatingWizard";
import OnboardingLayout from "./components/OnboardingLayout";
import SyncStatus from "./components/SyncStatus";
import UpdateBanner from "./components/UpdateBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import MemberErrorBoundary from "./components/member/MemberErrorBoundary";
import { crashLogger } from "./lib/crash-logger";
import { initSentry } from "./lib/sentry";
import { pruneCache } from "./lib/cache";
import { analytics } from "./lib/analytics";
import { ThemeProvider } from "./contexts/ThemeContext";

// Sentry must be initialized before crashLogger so the reporter is wired before any errors fire.
initSentry();
// Register global error/rejection handlers as early as possible.
crashLogger.init();
// Prune Dexie cache entries older than 7 days once per session startup.
pruneCache().catch(() => {});
// Repair any corrupted outbox items from previous crashes (unknown action, bad dependsOnTempIds).
// Marks unrecoverable items as permanent failures so they surface in the error UI
// rather than blocking healthy items indefinitely.
repairOutbox().catch(() => {});

// Bottom-nav primary pages — eager loaded for instant tab switch
import Dashboard from "./pages/Dashboard";
import Churches from "./pages/Churches";
import Families from "./pages/Families";
import Visits from "./pages/Visits";
import Prayers from "./pages/Prayers";
import District from "./pages/District";
import Notes from "./pages/Notes";
import Agenda from "./pages/Agenda";

// Secondary pages — lazy loaded
const ChurchDetail   = lazy(() => import("./pages/ChurchDetail"));
const ChurchForm     = lazy(() => import("./pages/ChurchForm"));
const FamilyDetail   = lazy(() => import("./pages/FamilyDetail"));
const FamilyForm     = lazy(() => import("./pages/FamilyForm"));
const PersonDetail   = lazy(() => import("./pages/PersonDetail"));
const PersonForm     = lazy(() => import("./pages/PersonForm"));
const MovePerson     = lazy(() => import("./pages/MovePerson"));
const VisitForm      = lazy(() => import("./pages/VisitForm"));
const VisitDetail    = lazy(() => import("./pages/VisitDetail"));
const EventForm      = lazy(() => import("./pages/EventForm"));
const EventDetail    = lazy(() => import("./pages/EventDetail"));
const PrayerForm     = lazy(() => import("./pages/PrayerForm"));
const PrayerDetail   = lazy(() => import("./pages/PrayerDetail"));
const NoteEditor     = lazy(() => import("./pages/NoteEditor"));
const Settings        = lazy(() => import("./pages/Settings"));
const SettingsLists   = lazy(() => import("./pages/SettingsLists"));
const SettingsPlan    = lazy(() => import("./pages/SettingsPlan"));
const SettingsBilling = lazy(() => import("./pages/SettingsBilling"));
const SettingsSupport = lazy(() => import("./pages/SettingsSupport"));
const SettingsAbout   = lazy(() => import("./pages/SettingsAbout"));
const BillingSuccess  = lazy(() => import("./pages/BillingSuccess"));
const Profile        = lazy(() => import("./pages/Profile"));
const Preaching         = lazy(() => import("./pages/Preaching"));
const PreachingDetail   = lazy(() => import("./pages/PreachingDetail"));
const PreachingForm     = lazy(() => import("./pages/PreachingForm"));
const SermonPlanner     = lazy(() => import("./pages/SermonPlanner"));
const ChurchAgenda   = lazy(() => import("./pages/ChurchAgenda"));
const ChurchFamilies = lazy(() => import("./pages/ChurchFamilies"));
const ChurchDirectiva  = lazy(() => import("./pages/ChurchDirectiva"));
const JuntaDirectiva   = lazy(() => import("./pages/JuntaDirectiva"));
const ChurchMinistries   = lazy(() => import("./pages/ChurchMinistries"));
const ChurchPreachings   = lazy(() => import("./pages/ChurchPreachings"));
const ChurchSchedule     = lazy(() => import("./pages/ChurchSchedule"));
const ChurchReports    = lazy(() => import("./pages/ChurchReports"));
const AdminUsers       = lazy(() => import("./features/admin/pages/AdminUsers"));
const AdminReports     = lazy(() => import("./features/admin/pages/AdminReports"));
const AdminAudit       = lazy(() => import("./features/admin/pages/AdminAudit"));
const AdminOperations  = lazy(() => import("./features/admin/pages/AdminOperations"));
const AdminCases       = lazy(() => import("./features/admin/pages/AdminCases"));
const AgendaGlobal     = lazy(() => import("./pages/AgendaGlobal"));
const MinistryDetail   = lazy(() => import("./pages/MinistryDetail"));
const Materials             = lazy(() => import("./pages/Materials"));
const MaterialInventory     = lazy(() => import("./pages/MaterialInventory"));
const MaterialDetail        = lazy(() => import("./pages/MaterialDetail"));
const MaterialForm          = lazy(() => import("./pages/MaterialForm"));
const MaterialMovementForm  = lazy(() => import("./pages/MaterialMovementForm"));
const LocalInventoryForm     = lazy(() => import("./pages/LocalInventoryForm"));
const LocalInventoryOverview = lazy(() => import("./pages/LocalInventoryOverview"));
const MaterialDistributions       = lazy(() => import("./pages/MaterialDistributions"));
const MaterialDistributionForm    = lazy(() => import("./pages/MaterialDistributionForm"));
const MaterialDistributionDetail  = lazy(() => import("./pages/MaterialDistributionDetail"));
const MaterialSubscriptions           = lazy(() => import("./pages/MaterialSubscriptions"));
const MaterialSubscriptionForm        = lazy(() => import("./pages/MaterialSubscriptionForm"));
const MaterialSubscriptionDetail      = lazy(() => import("./pages/MaterialSubscriptionDetail"));
const MaterialsFinancialDashboard     = lazy(() => import("./pages/MaterialsFinancialDashboard"));
const ChurchMaterialsAccount          = lazy(() => import("./pages/ChurchMaterialsAccount"));
const InventoryHealth                 = lazy(() => import("./pages/InventoryHealth"));
const Evangelism                       = lazy(() => import("./pages/Evangelism"));
const SmallGroups                      = lazy(() => import("./pages/SmallGroups"));
const SmallGroupForm                   = lazy(() => import("./pages/SmallGroupForm"));
const SmallGroupDetail                 = lazy(() => import("./pages/SmallGroupDetail"));
const BibleStudiesList                 = lazy(() => import("./pages/BibleStudiesList"));
const Baptisms                         = lazy(() => import("./pages/Baptisms"));
const BibleStudy                       = lazy(() => import("./pages/BibleStudy"));
const AdministrativeReview           = lazy(() => import("./pages/AdministrativeReview"));
const ChurchAdministrativeReview     = lazy(() => import("./pages/ChurchAdministrativeReview"));
const DistrictAdministrativeReview   = lazy(() => import("./pages/DistrictAdministrativeReview"));
const DistrictTeam                 = lazy(() => import("./pages/DistrictTeam"));
const DistrictReports              = lazy(() => import("./pages/DistrictReports"));
const KeviaChat                    = lazy(() => import("./features/kevia/pages/KeviaChat"));
const DistrictExpenses             = lazy(() => import("./pages/DistrictExpenses"));
const DistrictExpenseForm          = lazy(() => import("./pages/DistrictExpenseForm"));
const CoordinatorDashboard         = lazy(() => import("./pages/CoordinatorDashboard"));
const MemberPortal                 = lazy(() => import("./pages/MemberPortal"));
const MemberAccess                 = lazy(() => import("./pages/MemberAccess"));
const Announcements                = lazy(() => import("./pages/Announcements"));
const AnnouncementForm             = lazy(() => import("./pages/AnnouncementForm"));
const Challenges                   = lazy(() => import("./pages/Challenges"));
const ChallengeForm                = lazy(() => import("./pages/ChallengeForm"));
const MemberMessages               = lazy(() => import("./pages/MemberMessages"));
const MemberIglesia                = lazy(() => import("./pages/member/MemberIglesia"));
const MemberEspiritual             = lazy(() => import("./pages/member/MemberEspiritual"));
const MemberVida                   = lazy(() => import("./pages/member/MemberVida"));
const MemberMayordomia             = lazy(() => import("./pages/member/MemberMayordomia"));
const MemberMayordomiaHub          = lazy(() => import("./pages/member/MemberMayordomiaHub"));
const MemberMayordomiaTema         = lazy(() => import("./pages/member/MemberMayordomiaTema"));
const MemberComunidad              = lazy(() => import("./pages/member/MemberComunidad"));
const MemberSettings               = lazy(() => import("./pages/member/MemberSettings"));
const MemberHimnario               = lazy(() => import("./pages/member/MemberHimnario"));
const MemberCapitulo               = lazy(() => import("./pages/member/MemberCapitulo"));
const MemberBiblia                 = lazy(() => import("./pages/member/MemberBiblia"));
const MemberDevocional             = lazy(() => import("./pages/member/MemberDevocional"));
const MemberEscuela                = lazy(() => import("./pages/member/MemberEscuela"));
const MemberCreencias              = lazy(() => import("./pages/member/MemberCreencias"));
const MemberManual                 = lazy(() => import("./pages/member/MemberManual"));
const MemberOracion                = lazy(() => import("./pages/member/MemberOracion"));
const MemberSalud                  = lazy(() => import("./pages/member/MemberSalud"));
const MemberKevia                  = lazy(() => import("./pages/member/MemberKevia"));
const FeDeJesusList                = lazy(() => import("./pages/member/FeDeJesusList"));
const FeDeJesusLesson              = lazy(() => import("./pages/member/FeDeJesusLesson"));
const Creencias28Quiz              = lazy(() => import("./pages/member/Creencias28Quiz"));
const ManualIglesiaQuiz            = lazy(() => import("./pages/member/ManualIglesiaQuiz"));

import "./App.css";

/* =====================================================
   NAVIGATION TRACKER
   Pushes every route change to NavHistory so TopBar's
   back() can skip form routes and respect real history.
===================================================== */
function NavigationTracker() {
  const { key, pathname, search } = useLocation();
  useEffect(() => {
    NavHistory.push(pathname + search);
  }, [key, pathname, search]);
  return null;
}

/* =====================================================
   FLOATING STATE GUARD
   Shows wizard overlay when pastor's family has no church assignment.

   States:
     default  — wizard overlay (blocks navigation)
     bypassed — pastor dismissed for now; banner shown, app accessible
     (completed state removed — context ministry_status drives dismissal naturally)
===================================================== */
function FloatingStateGuard({ children }) {
  const { isFloating, mode, family, loading } = useFloatingState();
  const [bypassed, setBypassed] = useState(false);

  if (loading)     return children;
  if (!isFloating) return children;

  const bannerLabel = mode === "setup_new"
    ? "Configura tu hogar pastoral — Toca para completar"
    : "Incorporación pendiente — Toca para completar";

  if (bypassed) {
    return (
      <>
        {children}
        <div className="fixed bottom-[calc(72px+env(safe-area-inset-bottom))] left-0 right-0 z-50 px-4 pb-1">
          <button
            onClick={() => setBypassed(false)}
            className="w-full bg-amber-500 text-white rounded-[16px] px-4 py-3 text-[13px] font-semibold flex items-center gap-2.5 shadow-[0_4px_16px_rgba(245,158,11,0.35)] transition-all duration-200 active:scale-[0.98]"
          >
            <AlertCircle size={15} className="shrink-0" />
            <span className="flex-1 text-left">{bannerLabel}</span>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {children}
      <FloatingWizard
        family={family}
        mode={mode}
        onComplete={() => {}}
        onDismiss={() => setBypassed(true)}
      />
    </>
  );
}

/* =====================================================
   REQUIRE DISTRICT
===================================================== */
function RequireDistrict() {
  const { district, loading, userProfile, isCoordinator, user } = useDistrict();

  // After a hard-reload triggered by "Actualizar app" in Settings, DistrictProvider
  // may need extra time to re-fetch the profile from Supabase (SW cache was just
  // cleared). Without this grace period RequireDistrict could see loading=false +
  // district=null and wrongly redirect the pastor to /create-district.
  //
  // keves_just_updated is written by Settings.jsx immediately before reload().
  // We read it synchronously (useState initializer) so it's captured before any
  // effects can clear it. We then remove it and hold AppLoader until either the
  // district resolves or a 4-second hard timeout elapses.
  const [postUpdateGrace, setPostUpdateGrace] = useState(
    () => Boolean(localStorage.getItem('keves_just_updated'))
  );

  useEffect(() => {
    if (!postUpdateGrace) return;
    localStorage.removeItem('keves_just_updated');
    const timer = setTimeout(() => setPostUpdateGrace(false), 4000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Collapse grace as soon as DistrictProvider finishes loading successfully.
  useEffect(() => {
    if (postUpdateGrace && !loading && (district || userProfile)) {
      setPostUpdateGrace(false);
    }
  }, [loading, district, userProfile, postUpdateGrace]);

  // Seed churches into localCache so form pages can render their dropdowns offline.
  // Runs once per session whenever the district is available and network is up.
  useEffect(() => {
    if (!district?.id || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    getChurches(district.id).catch(() => {});
  }, [district?.id]);

  // Live sync: invalidate materials queries when any other user modifies shared data.
  useMaterialsRealtime(district?.id);

  if (loading || postUpdateGrace) return <AppLoader />;

  // ✅ ADMIN puede entrar aunque district aún sea null — SALVO que se haya soltado
  // de su distrito (release_pastor_district): ahí debe caer al mismo redirect a
  // /create-district que ve un pastor normal sin distrito (bloque de abajo).
  if (userProfile?.role === "admin" && userProfile?.district_id) {
    return <Outlet />;
  }

  // ✅ COORDINADOR — tiene onboarding completo, nunca debe crear un distrito.
  // Su district_id se resuelve vía church_id fallback en DistrictProvider.
  if (isCoordinator) {
    return <Outlet />;
  }

  // Sesiones anónimas (miembros / invitados) → portal de miembros
  if (user?.is_anonymous) {
    return <Navigate to="/member" replace />;
  }

  // Solo redirigir si el perfil confirma que el pastor no tiene distrito.
  // Si userProfile es null o district_id existe pero el district aún no cargó
  // (error de red, carga incompleta), mostrar AppLoader en lugar de redirigir.
  if (!district) {
    if (userProfile && !userProfile.district_id) {
      return <Navigate to="/create-district" replace />;
    }
    // Miembro con cuenta real (convertido de anónimo): sin perfil de pastor → portal
    if (!userProfile && user && localStorage.getItem('member_church_id')) {
      return <Navigate to="/member" replace />;
    }
    // Defense-in-depth: role='member' reached here despite district-context guard
    if (userProfile?.account_status === 'pending' && localStorage.getItem('member_church_id')) {
      supabase.auth.signOut();
      return <Navigate to="/login" replace />;
    }
    // Real member account (district_id present but no pastoral district loaded)
    if (user && localStorage.getItem('member_church_id')) {
      supabase.auth.signOut();
      return <Navigate to="/member" replace />;
    }
    return <AppLoader />;
  }

  return <Outlet />;
}

/* =====================================================
   REQUIRE ADMIN
===================================================== */
function RequireAdmin() {
  const { loading, userProfile } = useDistrict();

  if (loading) return <AppLoader />;

  if (userProfile?.role !== "admin") {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}

// Blocks coordinators from district-only areas.
// Pastors and admins pass through unchanged.
function RequireNotCoordinator() {
  const { loading, isCoordinator } = useDistrict();

  if (loading) return <AppLoader />;

  if (isCoordinator) return <Navigate to="/" replace />;

  return <Outlet />;
}

/* =====================================================
   COORDINATOR AWARE HOME
   Redirects coordinators straight to their church detail
   so they skip the district-wide dashboard.
===================================================== */
function CoordinatorAwareHome() {
  const { isCoordinator } = useDistrict();
  if (isCoordinator) {
    return <Navigate to="/coordinator-dashboard" replace />;
  }
  return <Dashboard />;
}

/* =====================================================
   AUTH LOGGER (dev-only)
   Structured logs for every auth event. Silent in production.
   Usage: authLog("EVENT_NAME", { key: value, ... })
===================================================== */
const isDev = process.env.NODE_ENV !== "production";
const authLog = isDev
  ? (event, data) =>
      console.log(
        `%c[auth] %c${event}`,
        "color:#64748b;font-weight:normal",
        "color:#1b4f9c;font-weight:bold",
        { ts: new Date().toISOString().slice(11, 23), ...data }
      )
  : () => {};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Detect iOS resume: keves_last_active is written on pagehide (survives iOS
  // WebView kills, unlike sessionStorage). If it exists and is < 30 min old,
  // the user is returning mid-session — skip the animated splash and show a
  // thin bg overlay while auth re-resolves from localStorage (~50ms on iOS).
  //
  // Also treat as resume when offline + cached session exists: covers the case
  // where iOS momentarily reports no network when returning from another app,
  // causing Supabase to fire SIGNED_OUT for anonymous member sessions before
  // connectivity is fully restored.
  const [isResume] = useState(() => {
    const last = localStorage.getItem('keves_last_active');
    if (last) {
      const elapsed = Date.now() - parseInt(last, 10);
      if (elapsed < 30 * 60 * 1000) return true; // 30 minutes
    }
    // Offline + cached session → skip full splash (member anon sessions)
    if (!navigator.onLine && localStorage.getItem('keves-auth')) return true;
    return false;
  });

  // Boot splash — shown once per cold-start until auth + district are both ready
  const [splashDone, setSplashDone] = useState(isResume);

  // ✅ evitar loop infinito del splash (post-auth splash — kept disabled)
  const [showSplash, setShowSplash] = useState(false);


  const [appReady, setAppReady] = useState(false);

  // True when the app loaded directly at /set-password (invite or recovery flow).
  // Prevents DistrictProvider from calling getOrLinkProfile before the user has
  // set a password — that would create a profile with wrong state or throw.
  // Cleared naturally when SetPassword calls window.location.href="/" (full reload).
  const [isOnboarding] = useState(() => window.location.pathname === '/set-password');

  const handleAppReady = useCallback(() => {
    authLog("APP_READY", { isOnboarding });
    setAppReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref so the loading/session effect can read splashDone without being in its
  // dependency array — prevents the safety-timeout → setLoading(false) path from
  // triggering setAppReady(false) and overriding the timeout's setAppReady(true).
  const splashDoneRef = useRef(isResume);
  useEffect(() => { splashDoneRef.current = splashDone; }, [splashDone]);

  // Sign out then hard-navigate to /login so recovery sessions don't loop back
  // to / via the login route guard (which redirects authenticated users to /).
  const handleEscape = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch {}
    window.location.href = '/login';
  }, []);

  // Kick off the Dashboard bundle download while SplashScreen is showing so
  // the lazy chunk is in memory by the time splashDone becomes true, preventing
  // AppLoader from ever flashing on initial navigation.
  useEffect(() => { import('./pages/Dashboard'); }, []);

  // When auth resolves with a real session, reset appReady so the splash waits
  // for the actual district/profile load (not the premature null-user onReady).
  //
  // GUARD: skip when isOnboarding (/set-password). In that flow DistrictProvider
  // receives user=null throughout and calls onReady() on mount. If we reset appReady
  // when loading resolves with a recovery/invite session, DistrictProvider won't
  // re-run (user?.id stays undefined) and appReady is stuck at false indefinitely.
  useEffect(() => {
    if (isOnboarding) return;
    if (!loading && session) {
      authLog("APP_READY_RESET", { reason: "auth-resolved-with-session" });
      // Guard: only reset appReady post-boot. Without this, the safety-timeout
      // calling markResolved() → setLoading(false) triggers this effect and
      // immediately overrides the timeout's own setAppReady(true).
      if (splashDoneRef.current) setAppReady(false);
      // setShowSplash is handled in the SIGNED_IN auth handler (below) so that
      // it only fires for actual login-form submissions, not token refreshes.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Emit session_end metric when backgrounded; probe session on resume.
  // On iOS/Android, the browser throttles timers while backgrounded — Supabase's
  // autoRefreshToken timer may not fire. Calling getSession() on resume forces an
  // immediate refresh attempt, which either issues TOKEN_REFRESHED or SIGNED_OUT.
  // Without this probe, an expired session isn't detected until the next Supabase
  // call (which could be minutes away on a quiet screen).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        analytics.sessionEnd();
        localStorage.setItem('keves_last_active', Date.now().toString());
      } else {
        supabase.auth.getSession().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // bfcache restore (iOS Safari / Chrome): the page is revived from memory with
  // full JS state intact. Clear any pending splash immediately.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setSplashDone(true);
        setAppReady(true);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Write a timestamp on every hide so iOS WebView kills (which clear
  // sessionStorage but preserve localStorage) can be detected on next mount.
  useEffect(() => {
    const onPageHide = () => {
      localStorage.setItem('keves_last_active', Date.now().toString());
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // Capture the PWA install prompt so Settings can surface it.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window.__kevesInstallPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Handle NAVIGATE messages posted by the service worker's notificationclick handler.
  // When the user taps a push notification, the SW posts { type: 'NAVIGATE', url }
  // to the focused client so the app navigates to the correct route.
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'NAVIGATE' && e.data?.url) {
        window.location.href = e.data.url;
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // When the network returns after an offline period, probe the session so the
  // token is refreshed if needed.
  //
  // Flow:
  //   getSession()     — reads the token from localStorage (no network call).
  //   refreshSession() — attempts a network refresh.
  //     • Success  → TOKEN_REFRESHED fires via onAuthStateChange → setSession updated ✅
  //     • Expired  → SIGNED_OUT fires via onAuthStateChange (now online) → normal logout ✅
  //
  // We do NOT set state here directly; onAuthStateChange is the single source of
  // truth for session state.
  useEffect(() => {
    const onOnline = () => {
      supabase.auth.getSession()
        .then(({ data: { session: s } }) => {
          if (s) supabase.auth.refreshSession().catch(() => {});
        })
        .catch(() => {});
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  /* =====================================================
     INIT AUTH
     Official Supabase v2 pattern:
     - onAuthStateChange fires INITIAL_SESSION synchronously
       with whatever is in storage (or null).
     - For email links (invite/recovery), Supabase exchanges the
       hash tokens and fires SIGNED_IN / PASSWORD_RECOVERY.
     - We keep loading=true until the relevant event fires so
       the splash screen covers any intermediate null-session state.
     - Safety timeout at 8s prevents infinite loading on edge cases.
  ===================================================== */
  useEffect(() => {
    let mounted    = true;
    let resolved   = false;
    let redirected = false; // guard: only one hard-nav redirect per mount

    // Capture link type BEFORE Supabase has a chance to clear the URL.
    // Supports both token flows:
    //   Implicit (pre-v2.65): tokens in hash — #access_token=...&type=recovery
    //   PKCE     (v2.65+):    code in search — ?code=...
    const hash         = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const isEmailLink  =
      (hash.includes("access_token") &&
        (hash.includes("type=invite") || hash.includes("type=recovery"))) ||
      searchParams.has("code");
    const emailLinkType =
      hash.includes("type=recovery") ||
      searchParams.get("type") === "recovery"
        ? "recovery"
        : "invite";

    authLog("INIT", {
      pathname: window.location.pathname,
      isOnboarding,
      isEmailLink,
      emailLinkType,
      hasLocalStorage,
    });

    // Idempotent: marks auth as resolved and unblocks the splash exactly once.
    const markResolved = () => {
      if (!resolved && mounted) {
        resolved = true;
        authLog("RESOLVED", { pathname: window.location.pathname });
        setLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;

      authLog(event, {
        pathname:     window.location.pathname,
        isOnboarding,
        isEmailLink,
        hasSession:   !!newSession,
        userId:       newSession?.user?.id?.slice(0, 8),
        resolved,
      });

      // ── Email-link flows: redirect via hard navigation ──────────────────────
      // MUST happen before any React state update and MUST use location.replace,
      // not window.history.replaceState.
      //
      // WHY replaceState is wrong here:
      //   replaceState changes the browser URL bar but does NOT fire a popstate
      //   event. React Router's BrowserRouter only re-renders on popstate — so
      //   the router keeps rendering '/' while the URL bar shows /set-password.
      //   The user bypasses SetPassword and lands directly in the main app shell.
      //
      // WHY location.replace is correct:
      //   It triggers a true page reload at the new path. React Router renders
      //   /set-password from mount. By the time the reload happens, Supabase has
      //   already persisted the session to localStorage, so SetPassword finds
      //   it via getSession() without needing the original hash tokens.
      //
      // EDGE CASE — no localStorage (Safari Private, rare WebViews):
      //   Supabase used in-memory storage. location.replace reloads the page and
      //   wipes in-memory state. SetPassword.jsx detects the missing session and
      //   shows a "modo privado" or "abre en Safari" message with context.
      //   This is the correct UX — we can't work around the browser limitation.
      if (event === "SIGNED_IN" && isEmailLink && emailLinkType === "invite") {
        if (redirected) return;
        redirected = true;
        window.location.replace("/set-password?type=invite");
        return; // page is reloading — no React state updates needed
      }
      if (event === "PASSWORD_RECOVERY") {
        if (redirected) return;
        redirected = true;
        window.location.replace("/set-password?type=recovery");
        return; // page is reloading — no React state updates needed
      }

      // ── OFFLINE SIGNED_OUT GUARD ──────────────────────────────────────────
      // When the device has no network, SIGNED_OUT almost always means the
      // JWT auto-refresh failed because the network is down (iOS cold-start
      // after an app kill), NOT that the user intentionally signed out or that
      // the refresh token is actually revoked.
      //
      // Returning early keeps the cached session intact so the app stays
      // accessible offline.  When the network returns, the 'online' effect
      // below probes the session; if the token is then confirmed invalid,
      // SIGNED_OUT fires again (this time with navigator.onLine === true) and
      // the normal logout path runs correctly.
      if (event === "SIGNED_OUT" && !navigator.onLine) {
        authLog("SIGNED_OUT_IGNORED", { reason: "offline — preserving cached session" });
        markResolved(); // idempotent — unblocks splash if INITIAL_SESSION never fired
        return;
      }

      // Sync session state for all events that reach this point.
      // Events that redirect (SIGNED_IN/invite, PASSWORD_RECOVERY) return early above.
      setSession(newSession);

      if (event === "INITIAL_SESSION") {
        // For normal page loads (no email link), this is our initial session.
        // For email links, INITIAL_SESSION fires with null before the exchange
        // completes — we intentionally keep loading=true until SIGNED_IN fires.
        if (!isEmailLink) markResolved();
      }

      if (event === "SIGNED_IN") {
        markResolved();
        // Post-boot login (user submitted the login form): loading is already
        // false so the [loading] effect never fires. Trigger the post-login
        // SplashScreen here instead. The splashDoneRef guard ensures this only
        // runs for mid-session logins, not cold-start INITIAL_SESSION flows,
        // and not token refreshes (which fire TOKEN_REFRESHED, not SIGNED_IN).
        if (splashDoneRef.current && !isResume) {
          setAppReady(false);
          setShowSplash(true);
        }
      }

      if (event === "USER_UPDATED") {
        // Fired after supabase.auth.updateUser() — session carries new tokens.
        // markResolved() is idempotent; safe to call even if already resolved.
        markResolved();
      }

      if (event === "SIGNED_OUT") {
        // Clear activity timestamp so the next login shows the full splash again.
        localStorage.removeItem('keves_last_active');
        // Clear all Dexie tables so stale data from this user can't leak to the
        // next user on the same device. Covers multi-tab logout (the tab that
        // didn't initiate the logout receives this event via storage broadcast)
        // and server-side session revocation (refresh token expired/revoked).
        // Fire-and-forget: redirect to /login happens immediately via route guard.
        clearLocalData().catch(() => {});
      }
    });

    // Safety net: if auth + district don't resolve in 8s (network failure, Supabase
    // down, DistrictProvider stall), force both loading and appReady so the user is
    // never stuck on the splash screen indefinitely.
    const safetyTimeout = setTimeout(() => {
      authLog("SAFETY_TIMEOUT", { resolved, pathname: window.location.pathname });
      markResolved();
      setAppReady(true);
    }, 8000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =====================================================
     APP
  ===================================================== */
  return (
    <ThemeProvider>
    <BrowserRouter>
      <ScrollToTop />
      <NavigationTracker />

      <DistrictProvider
        onReady={handleAppReady}
        user={isOnboarding ? null : (session?.user ?? null)}
      >
        <Toaster
          position="bottom-center"
          theme="system"
          expand={false}
          gap={8}
          offset="calc(96px + env(safe-area-inset-bottom))"
          toastOptions={{
            duration: 2000,
            style: {
              borderRadius: "20px",
              fontSize: "14px",
              fontWeight: "500",
              padding: "12px 16px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
              backdropFilter: "blur(12px)",
            },
            classNames: {
              toast: "keves-toast",
              success: "keves-toast--success",
              error: "keves-toast--error",
              warning: "keves-toast--warning",
              info: "keves-toast--info",
            },
          }}
        />
        <SyncStatus />
        <UpdateBanner />

        {/* ── Boot splash overlay ─────────────────────────────────────
             Stays on top (z-9999) while auth + district/profile are
             still loading. DistrictProvider bootstraps in the background.
             Only when both !loading AND appReady are true does the splash
             start its fade-out and ultimately set splashDone = true.
        ──────────────────────────────────────────────────────────── */}
        {!splashDone && (
          <SplashScreen
            ready={!loading && appReady}
            onFinish={() => { setSplashDone(true); setShowSplash(false); }}
            onEscape={handleEscape}
          />
        )}

        {/* iOS resume: thin opaque cover while auth + district profile re-resolve.
            Covers both the auth phase (loading) and the DistrictProvider phase
            (!appReady), preventing RequireDistrict from seeing userProfile=null
            and redirecting to /create-district before the profile loads. */}
        {isResume && (loading || !appReady) && (
          <div className="fixed inset-0 z-[9999] bg-[#f8fafc] dark:bg-[#050d1e]" />
        )}

        {/* ── Main app — deferred until splash is fully dismissed ─── */}
        {splashDone && (showSplash && session ? (
          <SplashScreen
            ready={appReady}
            onFinish={() => setShowSplash(false)}
            onEscape={handleEscape}
          />
        ) : (
          <ErrorBoundary>
          <Suspense fallback={<AppLoader />}>
            <Routes>
              {/* =====================================
                  LOGIN
              ===================================== */}
              <Route
                path="/login"
                element={
                  !session ? (
                    <Auth />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* =====================================
                  REGISTER
              ===================================== */}
              <Route
                path="/register"
                element={<Navigate to="/request-access" replace />}
              />

              {/* =====================================
                  ACCESS FLOW
              ===================================== */}
              <Route
                path="/request-access"
                element={<RequestAccess />}
              />

              <Route
                path="/request-sent"
                element={<RequestSent />}
              />

              {/* =====================================
                  ONBOARDING SHELL
                  Minimal layout — no bottom nav, no search,
                  no notifications. Used for invite + bootstrap flows.
              ===================================== */}
              <Route element={<OnboardingLayout />}>
                <Route path="/set-password" element={<SetPassword />} />
                <Route
                  element={session ? <Outlet /> : <Navigate to="/login" replace />}
                >
                  <Route path="/create-district" element={<CreateDistrict />} />
                </Route>
              </Route>

              <Route
                path="/check-email"
                element={<CheckEmail />}
              />

              <Route
                path="/forgot-password"
                element={<ForgotPassword />}
              />

              <Route
                path="/unauthorized"
                element={<Unauthorized />}
              />

              {/* =====================================
                  MEMBER PORTAL — completamente público,
                  sin guard de sesión ni de distrito.
                  Debe estar antes del bloque protegido
                  para que React Router lo evalúe primero.
              ===================================== */}
              <Route element={<MemberErrorBoundary><Outlet /></MemberErrorBoundary>}>
                <Route path="/member/access"     element={<MemberAccess />}    />
                <Route path="/member"            element={<MemberPortal />}    />
                <Route path="/member/iglesia"    element={<MemberIglesia />}   />
                <Route path="/member/espiritual" element={<MemberEspiritual />}/>
                <Route path="/member/vida"       element={<MemberVida />}      />
                <Route path="/member/mayordoma"  element={<MemberMayordomia />}/>
                <Route path="/member/mayordomia"       element={<MemberMayordomiaHub />}/>
                <Route path="/member/mayordomia/:tema" element={<MemberMayordomiaTema />}/>
                <Route path="/member/comunidad"  element={<MemberComunidad />} />
                <Route path="/member/settings"   element={<MemberSettings />}  />
                <Route path="/member/himnario"   element={<MemberHimnario />}  />
                <Route path="/member/capitulo"   element={<MemberCapitulo />}  />
                <Route path="/member/biblia"     element={<MemberBiblia />}    />
                <Route path="/member/devocional" element={<MemberDevocional />}/>
                <Route path="/member/escuela"    element={<MemberEscuela />}   />
                <Route path="/member/creencias"            element={<MemberCreencias />}  />
                <Route path="/member/creencias/quiz/:creenciaId" element={<Creencias28Quiz />} />
                <Route path="/member/manual"               element={<MemberManual />}     />
                <Route path="/member/manual/quiz/:capituloId" element={<ManualIglesiaQuiz />} />
                <Route path="/member/oracion"       element={<MemberOracion />}      />
                <Route path="/member/salud"         element={<MemberSalud />}        />
                <Route path="/member/kevia"         element={<MemberKevia />}        />
                <Route path="/member/fe-de-jesus"   element={<FeDeJesusList />}      />
                <Route path="/member/fe-de-jesus/:lessonId" element={<FeDeJesusLesson />} />
              </Route>

              {/* =====================================
                  PROTECTED APP
              ===================================== */}
              <Route
                element={
                  // Primary path: session is set → render app.
                  // Offline fallback: if session state is null but the device has
                  // no network AND localStorage still holds 'keves-auth', we are
                  // almost certainly in an offline cold-start where the SIGNED_OUT
                  // offline guard above failed to intercept in time.  Show the app
                  // rather than bouncing the user to /login without any network.
                  // When the network returns, the 'online' handler probes the token
                  // and redirects to /login only if it is truly expired.
                  (session || (!navigator.onLine && !!localStorage.getItem('keves-auth'))) ? (
                    <FloatingStateGuard>
                      <Layout />
                    </FloatingStateGuard>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                {/* =====================================
                    DISTRICT REQUIRED
                ===================================== */}
                <Route element={<RequireDistrict />}>
                  {/* DASHBOARD */}
                  <Route path="/" element={<CoordinatorAwareHome />} />
                  <Route path="/coordinator-dashboard" element={<CoordinatorDashboard />} />

                  {/* IGLESIAS */}
                  <Route
                    path="/churches"
                    element={<Churches />}
                  />

                  <Route element={<RequireNotCoordinator />}>
                    <Route
                      path="/churches/new"
                      element={<ChurchForm />}
                    />
                    <Route
                      path="/churches/:id/edit"
                      element={<ChurchForm />}
                    />
                  </Route>

                  <Route
                    path="/churches/:id"
                    element={<ChurchDetail />}
                  />

                  {/* EVENTOS IGLESIA */}
                  <Route
                    path="/churches/:id/events/new"
                    element={<EventForm type="church" />}
                  />

                  <Route
                    path="/churches/:id/events/:eventId/edit"
                    element={
                      <EventForm
                        type="church"
                        mode="edit"
                      />
                    }
                  />

                  {/* FAMILIAS */}
                  <Route
                    path="/families"
                    element={<Families />}
                  />

                  <Route
                    path="/families/new"
                    element={<FamilyForm />}
                  />

                  <Route
                    path="/families/:familyId"
                    element={<FamilyDetail />}
                  />

                  <Route
                    path="/families/:familyId/edit"
                    element={<FamilyForm />}
                  />

                  <Route
                    path="/families/:familyId/people/new"
                    element={<PersonForm />}
                  />

                  <Route
                    path="/churches/:id/families/new"
                    element={<FamilyForm />}
                  />

                  {/* PERSONAS */}
                  <Route
                    path="/people/new"
                    element={<PersonForm />}
                  />

                  <Route
                    path="/people/:id"
                    element={<PersonDetail />}
                  />

                  <Route
                    path="/people/:id/edit"
                    element={<PersonForm />}
                  />

                  <Route
                    path="/people/:id/move-family"
                    element={<MovePerson />}
                  />

                  {/* VISITAS */}
                  <Route
                    path="/visits"
                    element={<Visits />}
                  />

                  <Route
                    path="/visits/new"
                    element={<VisitForm />}
                  />

                  <Route
                    path="/visits/:id"
                    element={<VisitDetail />}
                  />

                  <Route
                    path="/visits/:id/edit"
                    element={<VisitForm />}
                  />

                  {/* AGENDA */}
                  <Route
                    path="/agenda"
                    element={<Agenda />}
                  />
                  <Route
                    path="/agenda/global"
                    element={<AgendaGlobal />}
                  />
                  {/* EVENT DETAIL */}
<Route
  path="/agenda/field/:id"
  element={<EventDetail type="field" />}
/>

<Route
  path="/agenda/personal/:id"
  element={<EventDetail type="personal" />}
/>

<Route
  path="/churches/:churchId/events/:id"
  element={<EventDetail type="church" />}
/>

                  <Route element={<RequireNotCoordinator />}>
                    <Route
                      path="/agenda/field/new"
                      element={<EventForm type="field" />}
                    />
                    <Route
                      path="/agenda/field/:id/edit"
                      element={<EventForm type="field" mode="edit" />}
                    />
                  </Route>

                  <Route
                    path="/agenda/personal/new"
                    element={<EventForm type="personal" />}
                  />

                  <Route
                    path="/agenda/personal/:id/edit"
                    element={
                      <EventForm
                        type="personal"
                        mode="edit"
                      />
                    }
                  />

                  {/* ORACIONES */}
                  <Route
                    path="/prayers"
                    element={<Prayers />}
                  />

                  <Route
                    path="/prayers/new"
                    element={<PrayerForm />}
                  />

                  <Route
                    path="/prayers/:id"
                    element={<PrayerDetail />}
                  />

                  <Route
                    path="/prayers/:id/edit"
                    element={<PrayerForm />}
                  />

                  {/* DISTRITO — coordinators blocked */}
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/district"         element={<District />}         />
                    <Route path="/district/team"    element={<DistrictTeam />}    />
                    <Route path="/district/reports" element={<DistrictReports />} />
                  </Route>


                  {/* NOTAS — coordinators blocked */}
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/notes"     element={<Notes />}      />
                    <Route path="/notes/new" element={<NoteEditor />} />
                    <Route path="/notes/:id" element={<NoteEditor />} />
                  </Route>

                  {/* PERFIL */}
                  <Route
                    path="/profile"
                    element={<Profile />}
                  />

                  {/* SETTINGS */}
                  <Route path="/settings"          element={<Settings />}         />
                  <Route path="/settings/lists"    element={<SettingsLists />}    />
                  <Route path="/settings/plan"     element={<SettingsPlan />}     />
                  <Route path="/settings/support"  element={<SettingsSupport />}  />
                  <Route path="/settings/about"    element={<SettingsAbout />}    />
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/settings/billing"  element={<SettingsBilling />}  />
                    <Route path="/billing/success"   element={<BillingSuccess />}   />
                  </Route>

                  {/* ADMIN */}
                  <Route element={<RequireAdmin />}>
                    <Route path="/admin/users"       element={<AdminUsers />}      />
                    <Route path="/admin/reports"     element={<AdminReports />}    />
                    <Route path="/admin/audit"       element={<AdminAudit />}      />
                    <Route path="/admin/operations"  element={<AdminOperations />} />
                    <Route path="/admin/cases"       element={<AdminCases />}      />
                  </Route>

                  {/* DIRECTIVA */}
                  <Route
                    path="/churches/:id/directiva"
                    element={<ChurchDirectiva />}
                  />

                  {/* JUNTA DIRECTIVA */}
                  <Route
                    path="/churches/:id/board"
                    element={<JuntaDirectiva />}
                  />

                  {/* PREDICACIONES POR IGLESIA */}
                  <Route
                    path="/churches/:id/preaching"
                    element={<ChurchPreachings />}
                  />

                  {/* HORARIOS DE SERVICIO */}
                  <Route
                    path="/churches/:id/schedule"
                    element={<ChurchSchedule />}
                  />

                  {/* MINISTERIOS */}
                  <Route
                    path="/churches/:id/ministries/edit"
                    element={<ChurchMinistries />}
                  />
                  <Route
                    path="/churches/:id/ministries/:dept"
                    element={<MinistryDetail />}
                  />

                  {/* AGENDA IGLESIAS */}
                  <Route
                    path="/churches/:id/agenda"
                    element={<ChurchAgenda />}
                  />

                  <Route
                    path="/churches/:id/families"
                    element={<ChurchFamilies />}
                  />

                  <Route
                    path="/churches/:id/reports"
                    element={<ChurchReports />}
                  />

                  {/* MATERIALES — coordinators blocked */}
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/materials"                     element={<Materials />}                    />
                    <Route path="/materials/inventory"           element={<MaterialInventory />}            />
                    <Route path="/materials/new"                 element={<MaterialForm />}                 />
                    <Route path="/materials/:id"                 element={<MaterialDetail />}               />
                    <Route path="/materials/:id/edit"            element={<MaterialForm />}                 />
                    <Route path="/materials/:id/movement/new"    element={<MaterialMovementForm />}         />
                    <Route path="/materials/:id/local-stock"     element={<LocalInventoryForm />}           />
                    <Route path="/materials/local-inventory"     element={<LocalInventoryOverview />}       />

                    {/* DISTRIBUCIONES */}
                    <Route path="/materials/distributions"          element={<MaterialDistributions />}         />
                    <Route path="/materials/distributions/new"      element={<MaterialDistributionForm />}      />
                    <Route path="/materials/distributions/:id"      element={<MaterialDistributionDetail />}    />
                    <Route path="/materials/distributions/:id/edit" element={<MaterialDistributionForm />}      />

                    {/* SUSCRIPCIONES */}
                    <Route path="/materials/subscriptions"          element={<MaterialSubscriptions />}         />
                    <Route path="/materials/subscriptions/new"      element={<MaterialSubscriptionForm />}      />
                    <Route path="/materials/subscriptions/:id"      element={<MaterialSubscriptionDetail />}    />
                    <Route path="/materials/subscriptions/:id/edit" element={<MaterialSubscriptionForm />}      />
                    <Route path="/materials/dashboard"              element={<MaterialsFinancialDashboard />}   />
                    <Route path="/materials/health"                 element={<InventoryHealth />}               />

                    {/* GASTOS DEL DISTRITO — bounded context aislado */}
                    <Route path="/district-expenses"          element={<DistrictExpenses />}    />
                    <Route path="/district-expenses/new"      element={<DistrictExpenseForm />} />
                    <Route path="/district-expenses/:id/edit" element={<DistrictExpenseForm />} />
                  </Route>

                  {/* ChurchMaterialsAccount está fuera de RequireNotCoordinator
                      intencionalmente: el coordinador puede ver (solo lectura) la cuenta de
                      materiales de SU iglesia. El guard interno en ChurchMaterialsAccount.jsx
                      redirige si intenta ver otra iglesia. El pastor sigue teniendo acceso
                      completo al módulo de materiales desde /materials/*. */}
                  <Route path="/churches/:id/materials/account" element={<ChurchMaterialsAccount />} />

                  {/* EVANGELISMO Y COMUNIDAD */}
                  <Route path="/evangelism"                       element={<Evangelism />} />
                  <Route path="/evangelism/groups"                element={<SmallGroups />} />
                  <Route path="/evangelism/groups/new"            element={<SmallGroupForm />} />
                  <Route path="/evangelism/groups/:id"            element={<SmallGroupDetail />} />
                  <Route path="/evangelism/groups/:id/edit"       element={<SmallGroupForm />} />
                  <Route path="/evangelism/bible-studies"         element={<BibleStudiesList />} />

                  {/* BAUTISMOS */}
                  <Route path="/baptisms" element={<Baptisms />} />
                  <Route path="/baptisms/studies/:personId" element={<BibleStudy />} />

                  {/* REVISIÓN ADMINISTRATIVA — coordinators blocked */}
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/administrative-review"           element={<AdministrativeReview />}         />
                    <Route path="/administrative-review/district"  element={<DistrictAdministrativeReview />} />
                    <Route path="/administrative-review/:churchId" element={<ChurchAdministrativeReview />}   />
                  </Route>

                  {/* PREDICACIÓN */}
                  <Route path="/preaching" element={<Preaching />} />
                  <Route path="/preaching/new" element={<PreachingForm />} />
                  <Route path="/preaching/:id" element={<PreachingDetail />} />
                  <Route element={<RequireNotCoordinator />}>
                    <Route path="/sermon-planner" element={<SermonPlanner />} />
                  </Route>

                  {/* ANUNCIOS */}
                  <Route path="/announcements"           element={<Announcements />}    />
                  <Route path="/announcements/new"       element={<AnnouncementForm />} />
                  <Route path="/announcements/:id/edit"  element={<AnnouncementForm />} />

                  {/* RETOS SEMANALES */}
                  <Route path="/challenges"          element={<Challenges />}    />
                  <Route path="/challenges/new"      element={<ChallengeForm />} />
                  <Route path="/challenges/:id/edit" element={<ChallengeForm />} />

                  {/* MENSAJES DE MIEMBROS */}
                  <Route path="/member-messages" element={<MemberMessages />} />

                  {/* KEVIA — asistente IA */}
                  <Route path="/kevia" element={<KeviaChat />} />

                </Route>
              </Route>

              {/* =====================================
                  FALLBACK
              ===================================== */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={session ? "/" : "/login"}
                    replace
                  />
                }
              />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        ))}
      </DistrictProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;